import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import { theme } from '@Guidelines/Theme/theme';
import InterviewRecordingContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/InterviewRecordingContent';
import RecordingErrorBoundary from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/RecordingErrorBoundary';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	min-height: 100vh;
	padding: 2rem 1rem;
	background-color: ${theme.colors.canvas};
`;

/**
 * V-19. Validates the route segments before mounting `InterviewRecordingContent`: an invalid
 * dossier address redirects to the beneficiary list, a missing `localInterview` segment to that dossier
 * — neither ever reaches the capture hook
 * (`UI/Pages/Beneficiaries/BeneficiaryDetails/index.tsx` follows the same pattern). Whether
 * `localInterview` actually names an interview on this device is a different question, answered only
 * once the local store hydrates — that is `useInterviewCapture`'s `'interview-not-found'` phase, not
 * this component's concern.
 */
const InterviewRecording: React.FC = () => {
	const navigate = useNavigate();
	const { subject } = useSubjectAddressParams();
	const params = useParams<{ localInterview: string }>();

	const localInterviewId = params.localInterview;
	const isInvalidLocalInterviewId = localInterviewId === undefined;

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (isInvalidLocalInterviewId) {
			navigate(buildSubjectPath(subject));
		}
	}, [subject, isInvalidLocalInterviewId, navigate]);

	return (
		<div css={containerStyle}>
			{subject !== null && localInterviewId !== undefined ? (
				<RecordingErrorBoundary subject={subject} localInterviewId={localInterviewId}>
					<InterviewRecordingContent subject={subject} localInterviewId={localInterviewId} />
				</RecordingErrorBoundary>
			) : null}
		</div>
	);
};

export default InterviewRecording;
